import api from "./api";
import type {
  Manager,
  CreateManagerRequest,
  UpdateManagerRequest,
  AddManagerToRestaurantRequest,
  Restaurant,
  CreateRestaurantRequest,
  UpdateRestaurantRequest,
  Company,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  AdminLoginRequest,
  AdminLoginResponse,
  ManagerLoginResponse,
  SelectRestaurantRequest,
  SelectRestaurantResponse,
} from "../types";

class AdminService {
  // Test
  test() {
    return api().get("/Admin/test");
  }

  // Login - ASP.NET API PascalCase bekliyor olabilir
  login(email: string, password: string) {
    return api().post("/Admin/login", {
      Email: email,
      Password: password
    });
  }

  managerLogin(email: string, password: string) {
    return api().post("/Admin/manager-login", {
      Email: email,
      Password: password
    });
  }

  selectRestaurant(restaurantId) {
    return api().post("/Admin/select-restaurant", { restaurantId });
  }

  // Manager Management
  getManagers() {
    return api().get("/Admin/managers");
  }

  createManager(managerData) {
    return api().post("/Admin/managers/create", managerData);
  }

  updateManager(managerData) {
    return api().put("/Admin/managers/update", managerData);
  }

  deleteManager(id) {
    return api().delete(`/Admin/managers/delete/${id}`);
  }

  addManagerToRestaurant(managerData) {
    return api().post("/Admin/managers/add-to-restaurant", managerData);
  }

  removeManagerFromRestaurant(managerId, restaurantId) {
    return api().delete(`/Admin/managers/${managerId}/restaurants/${restaurantId}`);
  }

  getManagerRestaurants(managerId) {
    return api().get(`/Admin/managers/${managerId}/restaurants`);
  }

  // Restaurant Management
  getRestaurants() {
    return api().get("/Admin/restaurants");
  }

  createRestaurant(restaurantData) {
    return api().post("/Admin/restaurants/create", restaurantData);
  }

  updateRestaurant(restaurantData) {
    return api().put("/Admin/restaurants/update", restaurantData);
  }


  deleteRestaurant(id) {
    return api().delete(`/Admin/restaurants/delete/${id}`);
  }

  // Pending Restaurant Approval
  getPendingRestaurants() {
    return api().get("/Admin/restaurants/pending-approval");
  }

  approveRestaurant(restaurantId: string) {
    return api().post(`/Admin/restaurants/${restaurantId}/approve`);
  }

  rejectRestaurant(restaurantId: string) {
    return api().post(`/Admin/restaurants/${restaurantId}/reject`);
  }

  // Toggle restaurant active status - sadece isActive alanını günceller
  toggleRestaurantStatus(id: number, isActive: boolean) {
    return api().patch(`/Admin/restaurants/${id}/status`, { IsActive: isActive });
  }

  // Company Management
  getCompanies() {
    return api().get("/Admin/companies");
  }

  createCompany(companyData) {
    return api().post("/Admin/companies/create", companyData);
  }

  updateCompany(companyData) {
    return api().put("/Admin/companies/update", companyData);
  }

  deleteCompany(id) {
    return api().delete(`/Admin/companies/delete/${id}`);
  }

  // Test Data
  createMultiRestaurantTestData() {
    return api().post("/Admin/create-multi-restaurant-test-data");
  }

  // Database
  testDatabase() {
    return api().get("/Admin/test-database");
  }

  fixMobileDuplicates() {
    return api().post("/Admin/fix-mobile-duplicates");
  }

  // Company Logo Management
  uploadCompanyLogo(companyId: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return api().post(`/Admin/companies/${companyId}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  deleteCompanyLogo(companyId: number) {
    return api().delete(`/Admin/companies/${companyId}/logo`);
  }

  // Restaurant Logo Management
  uploadRestaurantLogo(restaurantId: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return api().post(`/Admin/restaurants/${restaurantId}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  deleteRestaurantLogo(restaurantId: number) {
    return api().delete(`/Admin/restaurants/${restaurantId}/logo`);
  }

  // Pending Manager Management
  getPendingManagers() {
    return api().get("/Admin/pending-managers");
  }

  approveManager(approvalData: {
    managerId: string;
    existingCompanyId?: string;
    existingRestaurantId?: string;
    newCompanyName?: string;
    newCompanyCode?: string;
    newRestaurantName?: string;
    newRestaurantCode?: string;
    newRestaurantAddress?: string;
    newRestaurantPhone?: string;
  }) {
    // ASP.NET otomatik camelCase -> PascalCase map eder
    return api().post("/Admin/approve-manager", approvalData);
  }

  rejectManager(managerId: string) {
    return api().delete(`/Admin/reject-manager/${managerId}`);
  }

  // ShortCode Management - QR kod kısaltma sistemi
  generateShortCodes() {
    // Mevcut restoranlar için toplu ShortCode üretimi
    return api().post("/Admin/generate-shortcodes");
  }

  regenerateShortCode(restaurantId: number) {
    // Tek restoran için ShortCode yenileme
    return api().post(`/Admin/restaurants/${restaurantId}/regenerate-shortcode`);
  }
}

export default new AdminService();
